import { Link } from 'react-router-dom';
import Search from 'ui/search';
import Plus from 'assets/icons/plus';
import TableFilter from 'components/table/filters';
import { useRef, useState, useMemo } from 'react';
import SimpleSelect from 'ui/select';
import { useCustomSearchParams } from 'pages/users/lib/useParams';
import { referralProgramStatusOptions } from 'utils/helpers/constVariables';
import Filter from 'components/table/filters/filter';
import { getReferralProgramStatus } from 'utils/helpers/get/getReferralProgramStatus';
import TreeSelect from 'antd/es/tree-select';
import { useGetObjectsQuery } from 'pages/facilities/api/queries/generated/GetObjects';
import { DefaultOptionType } from 'antd/es/select';

const ReferralProgramsFilters = () => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const [showFilter, setShowFilter] = useState(false);

  const { params, handleParams, setParams, resetFilter } = useCustomSearchParams();

  const statusParam = params.get('status');
  const objectIdParam = params.get('objectId');

  const [status, setStatus] = useState(statusParam);
  const [objectId, setObjectId] = useState<string[]>(objectIdParam ? objectIdParam.split(',') : []);

  const { data: objectsData } = useGetObjectsQuery({
    variables: {
      input: {
        limit: 100,
        offset: 0,
      },
    },
  });

  const selectedObjects = useMemo(() => {
    if (!objectsData?.getObjects.objects || !objectId.length) return [];

    const findObject = (objects: any[], id: string): any => {
      for (const obj of objects) {
        if (obj.id === id) return obj;
        if (obj.children) {
          const found = findObject(obj.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    return objectId
      .map(id => findObject(objectsData.getObjects.objects, id))
      .filter(Boolean)
      .map(obj => obj.name);
  }, [objectsData?.getObjects.objects, objectId]);

  const { SHOW_PARENT } = TreeSelect;

  const transformData = (objects: any[]): DefaultOptionType[] => {
    return objects.map(obj => ({
      label: obj.name,
      value: obj.id,
      children: obj.children ? transformData(obj.children) : undefined,
    }));
  };

  const tProps = {
    treeData: objectsData?.getObjects.objects ? transformData(objectsData.getObjects.objects) : [],
    fieldNames: {
      label: 'label',
      value: 'value',
      children: 'children',
    },
    treeCheckable: true,
    showCheckedStrategy: SHOW_PARENT,
    placeholder: 'Введите объект',
    style: {
      width: '100%',
    },
  };

  const handleFilterVisible = () => {
    setShowFilter(prevState => !prevState);
  };

  const handleFilterSubmit = () => {
    handleParams('status', status);
    handleParams('objectId', objectId.length > 0 ? objectId.join(',') : null);
    setParams(params);
    setShowFilter(false);
  };

  const handleResetFilter = () => {
    setStatus(null);
    setObjectId([]);
    setShowFilter(false);
    setParams();
  };

  let paramsCount = 0;

  const paramCounter = (name: string) => {
    const value = params.get(name);
    if (value && value !== '') {
      return paramsCount++;
    }
  };

  paramCounter('status');
  paramCounter('facilityGroupId');
  paramCounter('objectId');

  return (
    <div className="filter-bar flex justify-between">
      <div className="flex flex-col gap-y-2">
        <div className="flex gap-2 self-center relative">
          <Search />
          <button
            type="button"
            ref={btnRef}
            onClick={handleFilterVisible}
            className="btn-stroke flex gap-x-1 items-center"
          >
            Фильтр
            {Boolean(paramsCount) && (
              <span className="Button3 text-primary rounded-full bg-primary bg-opacity-20 px-[7.5px]">
                {paramsCount}
              </span>
            )}
          </button>

          <TableFilter show={showFilter} setShow={setShowFilter} triggerRef={btnRef}>
            <SimpleSelect
              label="Статус"
              onChange={value => {
                setStatus(value);
              }}
              value={status}
              placeholder="Не выбрано"
              allOption="Все"
              options={referralProgramStatusOptions}
            />
            <div className="flex flex-col gap-1">
              <span className="Subtitle2">Объект</span>
              <TreeSelect
                {...tProps}
                onChange={value => {
                  setObjectId(value);
                }}
                maxTagCount="responsive"
                value={objectId}
                filterTreeNode={(val, node) => node.label?.toString().includes(val) || false}
              />
            </div>
            <div className="flex gap-x-5">
              <button className="btn-primary" onClick={handleFilterSubmit}>
                Применить
              </button>
              <button className="btn-stroke xs:w-auto w-full" onClick={handleResetFilter}>
                Сбросить
              </button>
            </div>
          </TableFilter>
        </div>

        {Boolean(paramsCount) && (
          <div className="flex gap-2">
            <Filter
              show={!!statusParam}
              content={getReferralProgramStatus(status)}
              clickHandler={() => {
                setStatus(null);
                resetFilter('status');
              }}
            />
            <Filter
              show={!!objectIdParam}
              content={selectedObjects.join(', ')}
              clickHandler={() => {
                setObjectId([]);
                resetFilter('objectId');
              }}
            />
          </div>
        )}
      </div>
      <Link className="self-center" to="/offers/referrals/create">
        <button className="btn-primary flex items-center gap-x-2">
          <Plus className="text-smena_white" />
          Новая реферальная программа
        </button>
      </Link>
    </div>
  );
};

export default ReferralProgramsFilters;
